import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { Opportunity } from '../../shared/opportunity';

/**
 * What a ticket says about an event beyond its title, shared by the desktop
 * card and the phone's ticket: the stub's entry word, the access badges, the
 * short date, and the chips beside the time.
 */

/** The stub's bottom word: Free, the price, Registration, or See listing. */
export function entryWord(e: Opportunity): string {
  if (e.isFree === true) return 'Free';
  if (e.priceAmount != null) {
    const whole = Number.isInteger(e.priceAmount);
    const symbol = !e.priceCurrency || e.priceCurrency === 'USD' ? '$' : `${e.priceCurrency} `;
    return `${symbol}${whole ? e.priceAmount : e.priceAmount.toFixed(2)}`;
  }
  if (e.rsvpType === 'registration_required' || e.rsvpType === 'apply_to_attend') return 'Registration';
  return 'See listing';
}

/** True only: an unstated field is drawn as nothing, never as "no" (contract §2). */
export function accessBadges(e: Opportunity): string[] {
  const out: string[] = [];
  if (e.isAccessible === true) out.push('Accessible');
  if (e.noExperienceNeeded === true) out.push('No experience needed');
  if (e.isOutdoor === true) out.push('Outdoor');
  if (e.isOutdoor === false) out.push('Indoor');
  return out;
}

/** "Sat, Sep 26" */
export function shortDate(dayKey: string): string {
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export const moreDatesLabel = (n: number) => `+ ${n} more ${n === 1 ? 'date' : 'dates'}`;

/** Cancelled and full events can't be marked Going; Maybe stays. */
export const canGo = (e: Opportunity) => e.status !== 'cancelled' && e.capacityStatus !== 'full';

/** Beside the time: Cancelled (red), Full, or the under-18 warning. */
export const Chip = styled('span')({
  fontFamily: FONTS.body,
  fontSize: 9.5,
  fontWeight: 700,
  lineHeight: 1.4,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  padding: '1px 7px',
  borderRadius: 999,
  background: 'rgba(54,47,42,0.12)',
  color: BRAND.mocha,
  whiteSpace: 'nowrap',
  '&[data-kind="cancelled"]': { background: '#ef3d23', color: BRAND.parchment },
  '&[data-kind="full"]': { background: BRAND.mocha, color: BRAND.parchment },
  '&[data-kind="age"]': { background: '#f6d9a8', color: '#6b4a12' },
});

/** An access badge: outlined, quieter than a chip. */
export const Badge = styled('span')({
  fontFamily: FONTS.body,
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1.4,
  padding: '0 7px',
  borderRadius: 999,
  border: '1px solid rgba(54,47,42,0.35)',
  color: 'rgba(54,47,42,0.8)',
  whiteSpace: 'nowrap',
});

export const Badges = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
});
